/**
 * Facility inspections by trade: who inspects what, what they found, what it
 * cost to put right, and what it is likely to cost next year.
 *
 * Seven trades inspect the airport's facilities, each with its own contractor
 * and its own two forms — a short daily round and a detailed periodic
 * inspection. A result is one of five: in order, a minor, major or critical
 * defect, or not applicable. The condition of a facility for a trade is read
 * from its latest inspection; a defect found is raised as a work order in the
 * one register the whole platform shares, so it appears on the work-orders
 * board, in Operations control and on the facility's own history.
 *
 * Three rules this module keeps:
 *
 *  - A work order is found again by the facility id — its asset tag, or the
 *    "F11 · …" at the head of its title when the platform does not know the tag.
 *    The trade and the repair type are written into the order as plain lines,
 *    "Trade: …" and "Repair type: …", so any screen can read them back.
 *  - The forecast is arithmetic anyone can redo: the repairs of the last two
 *    years as a yearly rate, times a factor for the condition now, times the
 *    average cost. The factors are proposed, and the screen says so.
 *  - Sample records carry `sample: true` and are shown as sample. Nothing
 *    invented sits unmarked beside the record.
 */
import type { WorkOrder, WorkOrderPriority } from "./workOrders";
import { isOpen } from "./workOrders";
import { FACILITY_BY_ID, FACILITY_LIST, type FacilityRef } from "./facilityRegister";

/* ── the trades and their forms ───────────────────────────────────────── */

export type TradeId = "civil" | "electrical" | "electromech" | "fire" | "utilities" | "cleaning" | "pest";

export interface CheckItem {
  id: string;
  text: string;
}

export interface CheckSection {
  id: string;
  title: string;
  items: CheckItem[];
}

export interface Trade {
  id: TradeId;
  label: string;
  /** Who does it when no contractor has been named. */
  defaultContractor: string;
  daily: CheckItem[];
  detailed: CheckSection[];
  repairTypes: string[];
  /** Average cost of one repair, SAR, when there is no history to average — proposed. */
  typicalCost: number;
}

const items = (prefix: string, texts: string[]): CheckItem[] => texts.map((text, i) => ({ id: `${prefix}${i + 1}`, text }));
const section = (id: string, title: string, texts: string[]): CheckSection => ({ id, title, items: items(`${id}.`, texts) });

export const TRADES: readonly Trade[] = [
  {
    id: "civil",
    label: "Civil",
    defaultContractor: "Civil works contractor",
    typicalCost: 9500,
    daily: items("cd", [
      "External envelope and cladding free of visible damage",
      "No water ingress at roofs, gutters or ceilings",
      "Floors free of trip hazards, lifting or broken tiles",
      "Doors, shutters and windows open, close and lock",
      "Ceilings complete — no missing, stained or sagging tiles",
      "Drains, gullies and channels clear",
      "Signage and floor markings legible",
    ]),
    detailed: [
      section("cs", "Structure", [
        "Slabs, beams and columns free of structural cracking",
        "No spalling or exposed reinforcement",
        "Expansion and movement joints intact and sealed",
        "No sign of settlement or differential movement",
      ]),
      section("ce", "Envelope", [
        "Roof membrane and flashings intact",
        "Curtain wall and cladding sealants sound",
        "Glazing unbroken and gaskets in place",
        "Basement and below-ground waterproofing dry",
      ]),
      section("cf", "Finishes", [
        "Floor finishes sound and level",
        "Wall finishes and paint in good order",
        "Suspended ceilings complete and level",
        "Handrails and balustrades secure",
      ]),
      section("cx", "External works", [
        "Paving and kerbs level and unbroken",
        "Manholes and covers seated and intact",
        "Fences and gates sound and secure",
        "Car park and road markings legible",
      ]),
    ],
    repairTypes: [
      "Concrete repair",
      "Crack injection",
      "Roof waterproofing",
      "Sealant replacement",
      "Flooring repair",
      "Ceiling repair",
      "Painting",
      "Door and hardware repair",
      "Drainage clearing",
      "Paving repair",
    ],
  },
  {
    id: "electrical",
    label: "Electrical",
    defaultContractor: "Electrical contractor",
    typicalCost: 7200,
    daily: items("ed", [
      "Lighting working in all public and plant areas",
      "Panels closed, locked and labelled",
      "No overheating, burning smell or noise at switchgear",
      "UPS in normal mode, no alarms",
      "Emergency lighting indicators healthy",
      "No exposed cables or damaged sockets",
      "Standby generator in auto, no alarms",
    ]),
    detailed: [
      section("el", "LV distribution", [
        "Thermographic survey of panels shows no hot spots",
        "Breakers and protection settings as designed",
        "Cable trays and containment secure",
        "Circuit schedules current and posted",
      ]),
      section("eg", "Standby power", [
        "Generator load test passed",
        "Fuel level and fuel quality acceptable",
        "Starter batteries and charger healthy",
        "Automatic changeover tested",
      ]),
      section("eu", "UPS and batteries", [
        "UPS load within rating",
        "Battery autonomy test passed",
        "Battery room ventilation and temperature normal",
      ]),
      section("ei", "Lighting", [
        "Internal lighting levels adequate",
        "External and car park lighting working",
        "Emergency lighting duration test passed",
      ]),
      section("ee", "Earthing and lightning protection", [
        "Earth resistance within limit",
        "Lightning protection down conductors intact",
        "Equipotential bonding in place",
      ]),
    ],
    repairTypes: [
      "Breaker replacement",
      "Cable repair",
      "Luminaire replacement",
      "Termination tightening",
      "UPS battery replacement",
      "Generator service",
      "Earthing repair",
      "Panel repair",
    ],
  },
  {
    id: "electromech",
    label: "Electro-mechanical",
    defaultContractor: "Electro-mechanical (MEP) contractor",
    typicalCost: 11800,
    daily: items("md", [
      "Space temperatures within the set range",
      "Air handling units running without abnormal noise or vibration",
      "Chillers and pumps running, no alarms",
      "Lifts in service",
      "Escalators and travelators in service",
      "No visible leaks from pipework or units",
      "Building management system free of active alarms",
    ]),
    detailed: [
      section("mh", "HVAC", [
        "Filters clean or replaced to schedule",
        "Coils clean and fins undamaged",
        "Belts, couplings and bearings in good condition",
        "Dampers and actuators working",
        "Refrigerant charge and pressures normal",
      ]),
      section("mv", "Vertical transport", [
        "Lift brakes, doors and safety gear tested",
        "Lift emergency phone and alarm working",
        "Escalator steps, comb plates and handrails sound",
      ]),
      section("mp", "Pumps and motors", [
        "Vibration within limits",
        "Seals and glands not leaking",
        "Motor insulation resistance acceptable",
      ]),
      section("mc", "Controls", [
        "Sensors reading true against a reference",
        "Setpoints and schedules as agreed",
      ]),
      section("mb", "Baggage handling", [
        "Conveyor belts and rollers in good condition",
        "Drives and gearboxes quiet and cool",
        "Emergency stops tested",
      ]),
    ],
    repairTypes: [
      "Filter replacement",
      "Belt replacement",
      "Refrigerant recharge",
      "Motor or bearing replacement",
      "Lift door repair",
      "Escalator repair",
      "Controls calibration",
      "Pump seal replacement",
      "Conveyor repair",
    ],
  },
  {
    id: "fire",
    label: "Fire systems",
    defaultContractor: "Fire systems contractor",
    typicalCost: 6400,
    daily: items("fd", [
      "Fire alarm panel normal, no faults or disablements",
      "Extinguishers present, tagged and in date",
      "Escape routes and exits clear",
      "Fire doors closed and not wedged",
      "Sprinkler valves open and secured",
      "Hose reels accessible",
    ]),
    detailed: [
      section("fa", "Detection and alarm", [
        "Detectors tested — sample for this quarter",
        "Sounders and beacons audible and visible",
        "Panel batteries and charger healthy",
        "Cause and effect tested",
      ]),
      section("fs", "Suppression", [
        "Sprinkler flow test passed",
        "Fire pumps start automatically",
        "System pressures normal",
        "Gas suppression in server rooms charged",
      ]),
      section("fp", "Passive protection", [
        "Fire door closers and seals working",
        "Compartment penetrations sealed",
        "Fire dampers tested",
      ]),
      section("fe", "Means of escape", [
        "Exit signs illuminated",
        "Assembly points marked and clear",
      ]),
      section("fx", "Portable equipment", [
        "Extinguisher service dates current",
        "Fire blankets and hose reels serviced",
      ]),
    ],
    repairTypes: [
      "Detector replacement",
      "Panel repair",
      "Sprinkler head replacement",
      "Fire pump repair",
      "Door closer or seal repair",
      "Penetration sealing",
      "Extinguisher service",
      "Damper repair",
    ],
  },
  {
    id: "utilities",
    label: "Utilities",
    defaultContractor: "Utilities operator",
    typicalCost: 8600,
    daily: items("ud", [
      "Water pressure normal",
      "Tank levels within range",
      "Pumps running, no leaks",
      "Sewage treatment process normal, no odour",
      "Chilled water supply temperature normal",
      "No visible leaks on networks",
      "Meters read and recorded",
    ]),
    detailed: [
      section("uw", "Potable water", [
        "Tanks clean and covered",
        "Chlorine residual within range",
        "Backflow devices tested",
        "Hot and cold temperatures safe for legionella control",
      ]),
      section("us", "Drainage and sewage", [
        "Lift stations working, no alarms",
        "Manholes and interceptors clear",
        "Treatment aeration and clarifiers working",
        "Effluent quality within consent",
      ]),
      section("uc", "Chilled water network", [
        "Valves operable",
        "Pipe insulation intact",
        "Distribution pumps healthy",
      ]),
      section("um", "Metering", [
        "Meters working and read remotely",
        "Consumption consistent with last period",
      ]),
    ],
    repairTypes: [
      "Pipe leak repair",
      "Valve replacement",
      "Pump repair",
      "Tank cleaning",
      "Blockage clearing",
      "Insulation repair",
      "Meter replacement",
      "Chemical dosing adjustment",
    ],
  },
  {
    id: "cleaning",
    label: "Cleaning services",
    defaultContractor: "Terminal cleaning",
    typicalCost: 2400,
    daily: items("kd", [
      "Public areas clean",
      "Toilets clean and stocked",
      "Waste bins emptied",
      "Spills attended and wet-floor signs in place",
      "Gate areas and seating clean",
      "Prayer rooms clean",
      "Carpets and mats clean",
    ]),
    detailed: [
      section("kp", "Public areas", [
        "High-traffic floors deep cleaned to schedule",
        "Seating and fixtures clean",
        "Glazing clean inside",
      ]),
      section("kw", "Washrooms", [
        "Deep clean to schedule",
        "Consumables stocked",
        "Fixtures clean and working",
      ]),
      section("kb", "Back of house", [
        "Offices and staff areas clean",
        "Stores and plant rooms tidy",
      ]),
      section("kh", "High level and external", [
        "High-level surfaces and fittings clean",
        "External glazing clean",
      ]),
      section("kr", "Waste and performance", [
        "Waste segregated and removed on time",
        "Response times met",
        "Complaints closed within the agreed time",
      ]),
    ],
    repairTypes: [
      "Deep clean",
      "Carpet shampoo",
      "Glazing clean",
      "High-level clean",
      "Consumables restock",
      "Waste removal",
      "Stain removal",
      "Re-clean",
    ],
  },
  {
    id: "pest",
    label: "Pest control",
    defaultContractor: "Pest control contractor",
    typicalCost: 1800,
    daily: items("pd", [
      "No pest sightings reported",
      "Bait stations present and intact",
      "Insect light traps working",
      "No evidence in food areas",
      "Doors, screens and strip curtains proofed",
      "Waste areas clean and closed",
    ]),
    detailed: [
      section("pr", "Rodents", [
        "Bait stations inspected and counted",
        "No activity or droppings found",
        "External perimeter stations intact",
      ]),
      section("pi", "Insects", [
        "Insect light trap tubes and catch trays serviced",
        "No crawling insect activity",
      ]),
      section("pb", "Birds and wildlife", [
        "Bird proofing — nets and spikes — intact",
        "No nests in or near the building",
        "Airside attractants removed",
      ]),
      section("pp", "Proofing", [
        "Gaps and openings sealed",
        "Drain covers and door sweeps in place",
      ]),
      section("pk", "Records", [
        "Treatments logged",
        "Only approved chemicals used, with safety data sheets on site",
      ]),
    ],
    repairTypes: [
      "Bait replacement",
      "Insect trap tube replacement",
      "Proofing repair",
      "Treatment",
      "Bird proofing repair",
      "Nest removal",
      "Fumigation",
    ],
  },
];

export const TRADE_BY_ID: ReadonlyMap<TradeId, Trade> = new Map(TRADES.map((t) => [t.id, t]));
export const tradeOf = (id: TradeId): Trade => TRADE_BY_ID.get(id)!;

export type FormKind = "daily" | "detailed";
export const FORM_LABEL: Record<FormKind, string> = { daily: "Daily inspection", detailed: "Detailed inspection" };

/** Every item on a form, flat, with its section when it has one. */
export function formItems(trade: Trade, form: FormKind): (CheckItem & { section?: string })[] {
  return form === "daily" ? trade.daily : trade.detailed.flatMap((s) => s.items.map((i) => ({ ...i, section: s.title })));
}

/* ── results, and the condition they add up to ────────────────────────── */

export type Result = "ok" | "minor" | "major" | "critical" | "na";
export const RESULTS: readonly Result[] = ["ok", "minor", "major", "critical", "na"];
export const RESULT_LABEL: Record<Result, string> = {
  ok: "OK",
  minor: "Minor",
  major: "Major",
  critical: "Critical",
  na: "N/A",
};
const RESULT_SCORE: Record<Exclude<Result, "na">, number> = { ok: 100, minor: 70, major: 40, critical: 10 };

export const isDefect = (r: Result) => r === "minor" || r === "major" || r === "critical";

/** One item that was not simply OK. Everything unlisted on the form was OK. */
export interface Fault {
  itemId: string;
  result: Exclude<Result, "ok">;
  note?: string;
  repairType?: string;
  /** the work order raised for it, once one is */
  workOrderId?: string;
  workOrderNumber?: string;
  /**
   * Its work order is completed or verified — read from the work-order
   * register every time (`withWorkDone`), never saved with the inspection.
   * A finding whose work is done is no longer open.
   */
  workDone?: boolean;
}

export interface InspectionRecord {
  id: string;
  facilityId: string;
  trade: TradeId;
  form: FormKind;
  /** ISO time the inspection was done */
  at: string;
  inspector: string;
  company: string;
  faults: Fault[];
  remarks?: string;
  sample?: boolean;
}

export type Band = "very_good" | "good" | "fair" | "poor" | "very_poor";
export const BAND_LABEL: Record<Band, string> = {
  very_good: "Very good",
  good: "Good",
  fair: "Fair",
  poor: "Poor",
  very_poor: "Very poor",
};
export const BANDS: readonly Band[] = ["very_good", "good", "fair", "poor", "very_poor"];

export function bandOf(score: number): Band {
  if (score >= 90) return "very_good";
  if (score >= 75) return "good";
  if (score >= 60) return "fair";
  if (score >= 40) return "poor";
  return "very_poor";
}

/** 0–100: the mean of the items inspected, OK counting 100 and a critical defect 10; N/A left out. */
export function scoreOf(rec: InspectionRecord): number {
  const trade = tradeOf(rec.trade);
  const all = formItems(trade, rec.form);
  const byItem = new Map(rec.faults.map((f) => [f.itemId, f.result]));
  let sum = 0;
  let n = 0;
  for (const it of all) {
    const r = byItem.get(it.id) ?? "ok";
    if (r === "na") continue;
    sum += RESULT_SCORE[r];
    n += 1;
  }
  return n === 0 ? 100 : Math.round(sum / n);
}

export const defectsOf = (rec: InspectionRecord) => rec.faults.filter((f) => isDefect(f.result));

/* ── contractors, and the store ───────────────────────────────────────── */

export interface Contractor {
  company: string;
  /** a contract in the stakeholders' contracts register, when there is one */
  contractId?: string;
  contact?: string;
}

export interface RepairRecord {
  id: string;
  facilityId: string;
  trade: TradeId;
  repairType: string;
  openedAt: string;
  closedAt?: string;
  cost?: number;
  sample?: boolean;
}

export interface InspectionStore {
  version: 1;
  contractors: Partial<Record<TradeId, Contractor>>;
  inspections: InspectionRecord[];
  /** repairs recorded before this register — imported, or the sample */
  history: RepairRecord[];
}

/** The note topic the store is kept under; the Instruments bench reads it too. */
export const INSPECTION_TOPIC = "assets.facility-inspections";

export const EMPTY_STORE: InspectionStore = { version: 1, contractors: {}, inspections: [], history: [] };

export function contractorFor(store: InspectionStore, trade: TradeId): Contractor {
  return store.contractors[trade] ?? { company: tradeOf(trade).defaultContractor };
}

let counter = 0;
export function newInspectionId(now = Date.now()): string {
  counter = (counter + 1) % 1000;
  return `INS-${new Date(now).toISOString().slice(0, 10).replace(/-/g, "")}-${String(now % 100000).padStart(5, "0")}${counter}`;
}

/** The store with one inspection added or replaced. */
export function saveInspection(store: InspectionStore, rec: InspectionRecord): InspectionStore {
  const rest = store.inspections.filter((r) => r.id !== rec.id);
  return { ...store, inspections: [...rest, rec] };
}

/** Record the work order raised for a fault, so the finding and the order find each other. */
export function linkWorkOrder(store: InspectionStore, inspectionId: string, itemId: string, wo: Pick<WorkOrder, "id" | "number">): InspectionStore {
  return {
    ...store,
    inspections: store.inspections.map((r) =>
      r.id !== inspectionId
        ? r
        : { ...r, faults: r.faults.map((f) => (f.itemId === itemId ? { ...f, workOrderId: wo.id, workOrderNumber: wo.number } : f)) },
    ),
  };
}

/**
 * The store with each finding's work order looked up: a finding whose order is
 * completed or verified is marked done, and so no longer open (audit: findings
 * stayed open after the work was done). A cancelled order leaves it open.
 */
export function withWorkDone(store: InspectionStore, orders: readonly Pick<WorkOrder, "id" | "status">[]): InspectionStore {
  const done = new Set(orders.filter((o) => o.status === "completed" || o.status === "verified").map((o) => o.id));
  if (!done.size) return store;
  return {
    ...store,
    inspections: store.inspections.map((r) =>
      r.faults.some((f) => f.workOrderId && done.has(f.workOrderId))
        ? { ...r, faults: r.faults.map((f) => (f.workOrderId && done.has(f.workOrderId) ? { ...f, workDone: true } : f)) }
        : r,
    ),
  };
}

export function withoutSample(store: InspectionStore): InspectionStore {
  return { ...store, inspections: store.inspections.filter((r) => !r.sample), history: store.history.filter((h) => !h.sample) };
}

/* ── where each facility stands ───────────────────────────────────────── */

const byTime = (a: { at: string }, b: { at: string }) => (a.at < b.at ? -1 : a.at > b.at ? 1 : 0);

export function inspectionsFor(store: InspectionStore, facilityId?: string, trade?: TradeId): InspectionRecord[] {
  return store.inspections
    .filter((r) => (!facilityId || r.facilityId === facilityId) && (!trade || r.trade === trade))
    .sort(byTime);
}

export interface Standing {
  facility: FacilityRef;
  trade: Trade;
  /** the latest inspection of either form */
  latest: InspectionRecord | null;
  /** the inspection the condition is read from: the latest detailed one, else the latest daily round */
  basis: InspectionRecord | null;
  score: number | null;
  band: Band | null;
  /** the basis score minus the previous one of the same form, when there are two */
  change: number | null;
  /** defects still open: on the basis, and on any round since it — with no work order raised */
  open: OpenFinding[];
  unraised: number;
}

export interface OpenFinding {
  inspection: InspectionRecord;
  fault: Fault;
}

/*
 * The condition is read from the latest DETAILED inspection. A daily round
 * checks six or seven things; reading the condition from it swung a facility
 * from 91 to 53 on one dirty washroom. Daily rounds still count: whatever they
 * find since the detailed inspection is open until a work order is raised.
 */
export function standing(store: InspectionStore, facilityId: string, trade: TradeId): Standing {
  const facility = FACILITY_BY_ID.get(facilityId)!;
  const list = inspectionsFor(store, facilityId, trade);
  const latest = list[list.length - 1] ?? null;
  const detailed = list.filter((r) => r.form === "detailed");
  const basis = detailed[detailed.length - 1] ?? latest;
  const sameForm = basis ? list.filter((r) => r.form === basis.form) : [];
  const prev = sameForm[sameForm.length - 2] ?? null;
  const score = basis ? scoreOf(basis) : null;
  const since = basis ? list.filter((r) => r === basis || r.at > basis.at) : [];
  /* one finding per item: a later round's word on the same item replaces the earlier */
  const byItem = new Map<string, OpenFinding>();
  for (const r of since) for (const f of defectsOf(r)) byItem.set(f.itemId, { inspection: r, fault: f });
  /* a later round's word stands, and when its work order is done the finding is closed */
  const open = [...byItem.values()].filter((o) => !o.fault.workDone);
  return {
    facility,
    trade: tradeOf(trade),
    latest,
    basis,
    score,
    band: score === null ? null : bandOf(score),
    change: basis && prev ? score! - scoreOf(prev) : null,
    open,
    unraised: open.filter((o) => !o.fault.workOrderId).length,
  };
}

/* ── work orders, read back ───────────────────────────────────────────── */

export function workOrderTitle(f: FacilityRef, trade: Trade, what: string): string {
  return `${f.id} · ${f.name} — ${trade.label}: ${what}`;
}

export const PRIORITY_FOR: Record<Exclude<Result, "ok" | "na">, WorkOrderPriority> = { critical: 1, major: 2, minor: 3 };

/** Which facility an order belongs to: its asset tag, or the id at the head of its title. */
export function facilityOfOrder(o: Pick<WorkOrder, "assetTag" | "title">): string | null {
  if (o.assetTag && FACILITY_BY_ID.has(o.assetTag)) return o.assetTag;
  const m = o.title.match(/^(F\d{2}) · /);
  return m && FACILITY_BY_ID.has(m[1]) ? m[1] : null;
}

/** The trade an order was raised for, from its "Trade: …" line or its title. */
export function tradeOfOrder(o: Pick<WorkOrder, "title" | "description">): TradeId | null {
  const line = o.description?.match(/^Trade: (.+)$/m)?.[1]?.trim();
  for (const t of TRADES) {
    if (line === t.label) return t.id;
    if (o.title.includes(`— ${t.label}:`)) return t.id;
  }
  return null;
}

export const repairTypeOfOrder = (o: Pick<WorkOrder, "description">): string | null =>
  o.description?.match(/^Repair type: (.+)$/m)?.[1]?.trim() ?? null;

export function ordersFor(orders: readonly WorkOrder[], facilityId?: string, trade?: TradeId): WorkOrder[] {
  return orders.filter((o) => {
    const f = facilityOfOrder(o);
    if (!f || (facilityId && f !== facilityId)) return false;
    return !trade || tradeOfOrder(o) === trade;
  });
}

/* ── repairs: the history and the register together ───────────────────── */

export interface Repair {
  id: string;
  facilityId: string;
  trade: TradeId;
  repairType: string;
  openedAt: string;
  closedAt?: string;
  cost?: number;
  open: boolean;
  /** "sample", "history" (imported), or the work order number */
  source: string;
  sample: boolean;
}

export function repairs(store: InspectionStore, orders: readonly WorkOrder[]): Repair[] {
  const out: Repair[] = store.history.map((h) => ({
    id: h.id,
    facilityId: h.facilityId,
    trade: h.trade,
    repairType: h.repairType,
    openedAt: h.openedAt,
    closedAt: h.closedAt,
    cost: h.cost,
    open: !h.closedAt,
    source: h.sample ? "sample" : "history",
    sample: !!h.sample,
  }));
  for (const o of orders) {
    const facilityId = facilityOfOrder(o);
    const trade = tradeOfOrder(o);
    if (!facilityId || !trade || o.status === "cancelled") continue;
    out.push({
      id: o.id,
      facilityId,
      trade,
      repairType: repairTypeOfOrder(o) ?? "Not classified",
      openedAt: o.openedAt,
      closedAt: o.completedAt,
      cost: o.costActual ?? o.costPlanned,
      open: isOpen(o.status),
      source: o.number,
      sample: false,
    });
  }
  return out.sort((a, b) => (a.openedAt < b.openedAt ? -1 : 1));
}

export interface Tally {
  count: number;
  cost: number;
  /** repairs with a cost recorded — the average is over these only */
  costed: number;
}

const tally = (list: readonly Repair[]): Tally => ({
  count: list.length,
  cost: list.reduce((n, r) => n + (r.cost ?? 0), 0),
  costed: list.filter((r) => r.cost !== undefined).length,
});

export const averageCost = (t: Tally): number | null => (t.costed ? Math.round(t.cost / t.costed) : null);

/** Repairs by year and trade, the last `years` calendar years to date. */
export function byYear(list: readonly Repair[], now: Date, years = 3): { year: number; trades: Record<TradeId, Tally>; total: Tally }[] {
  const y0 = now.getFullYear();
  return Array.from({ length: years }, (_, i) => y0 - (years - 1) + i).map((year) => {
    const inYear = list.filter((r) => new Date(r.openedAt).getFullYear() === year);
    const trades = Object.fromEntries(TRADES.map((t) => [t.id, tally(inYear.filter((r) => r.trade === t.id))])) as Record<TradeId, Tally>;
    return { year, trades, total: tally(inYear) };
  });
}

/** Repairs by type, most frequent first. */
export function byRepairType(list: readonly Repair[]): { trade: TradeId; repairType: string; tally: Tally }[] {
  const groups = new Map<string, Repair[]>();
  for (const r of list) {
    const k = `${r.trade}|${r.repairType}`;
    groups.set(k, [...(groups.get(k) ?? []), r]);
  }
  return [...groups.entries()]
    .map(([k, rs]) => ({ trade: k.split("|")[0] as TradeId, repairType: k.split("|")[1], tally: tally(rs) }))
    .sort((a, b) => b.tally.count - a.tally.count || b.tally.cost - a.tally.cost);
}

/* ── the forecast ─────────────────────────────────────────────────────── */

/** How much likelier a repair is, by the condition now. Proposed, not calibrated. */
export const CONDITION_FACTOR: Record<Band | "none", number> = {
  very_good: 0.6,
  good: 0.8,
  fair: 1.0,
  poor: 1.3,
  very_poor: 1.6,
  none: 1.0,
};

/** How much of an average repair an open defect is taken to cost. Proposed. */
export const BACKLOG_WEIGHT = { critical: 1, major: 0.6, minor: 0.25 } as const;

export interface ForecastRow {
  facilityId: string;
  trade: TradeId;
  /** repairs in the last two years */
  recent: number;
  ratePerYear: number;
  band: Band | null;
  factor: number;
  expected: number;
  averageCost: number;
  /** the average came from this facility and trade's own costs, the trade's, or the proposed typical cost */
  costBasis: "own" | "trade" | "typical";
  /** open defects with no work order yet, weighted — a critical as one repair, a major 0.6, a minor 0.25 */
  backlog: number;
  budget: number;
}

export function forecast(store: InspectionStore, orders: readonly WorkOrder[], now: Date, trade?: TradeId): ForecastRow[] {
  const all = repairs(store, orders);
  const since = now.getTime() - 730 * 86_400_000;
  const tradeAvg = new Map(TRADES.map((t) => [t.id, averageCost(tally(all.filter((r) => r.trade === t.id)))]));
  const rows: ForecastRow[] = [];
  for (const f of FACILITY_LIST) {
    for (const t of TRADES) {
      if (trade && t.id !== trade) continue;
      const mine = all.filter((r) => r.facilityId === f.id && r.trade === t.id);
      const st = standing(store, f.id, t.id);
      if (mine.length === 0 && !st.latest) continue;
      const recent = mine.filter((r) => Date.parse(r.openedAt) >= since).length;
      const ratePerYear = recent / 2;
      const factor = CONDITION_FACTOR[st.band ?? "none"];
      const own = tally(mine);
      const [avg, costBasis] =
        own.costed >= 3
          ? [averageCost(own)!, "own" as const]
          : tradeAvg.get(t.id) !== null && tradeAvg.get(t.id) !== undefined
            ? [tradeAvg.get(t.id)!, "trade" as const]
            : [t.typicalCost, "typical" as const];
      const backlog =
        Math.round(
          st.open.filter((o) => !o.fault.workOrderId).reduce((n, o) => n + BACKLOG_WEIGHT[o.fault.result as keyof typeof BACKLOG_WEIGHT], 0) * 10,
        ) / 10;
      const expected = Math.round(ratePerYear * factor * 10) / 10;
      rows.push({
        facilityId: f.id,
        trade: t.id,
        recent,
        ratePerYear,
        band: st.band,
        factor,
        expected,
        averageCost: avg,
        costBasis,
        backlog,
        budget: Math.round((expected + backlog) * avg),
      });
    }
  }
  return rows.sort((a, b) => b.budget - a.budget);
}

/* ── reports ──────────────────────────────────────────────────────────── */

export type ReportLength = "short" | "long";

export interface ReportScope {
  facilityId: string | "all";
  trade: TradeId | "all";
  length: ReportLength;
}

export interface ReportLine {
  facility: FacilityRef;
  trade: Trade;
  standing: Standing;
  openOrders: WorkOrder[];
  /** long report only: every inspection of this pair, oldest first */
  inspections: InspectionRecord[];
  repairs: Repair[];
  forecast: ForecastRow | null;
}

/** What a report covers: every facility and trade in scope that has been inspected or repaired. */
export function reportLines(store: InspectionStore, orders: readonly WorkOrder[], scope: ReportScope, now: Date): ReportLine[] {
  const all = repairs(store, orders);
  const fc = forecast(store, orders, now);
  const facilities = scope.facilityId === "all" ? FACILITY_LIST : FACILITY_LIST.filter((f) => f.id === scope.facilityId);
  const trades = scope.trade === "all" ? TRADES : TRADES.filter((t) => t.id === scope.trade);
  const out: ReportLine[] = [];
  for (const f of facilities) {
    for (const t of trades) {
      const st = standing(store, f.id, t.id);
      const rs = all.filter((r) => r.facilityId === f.id && r.trade === t.id);
      if (!st.latest && rs.length === 0) continue;
      out.push({
        facility: f,
        trade: t,
        standing: st,
        openOrders: ordersFor(orders, f.id, t.id).filter((o) => isOpen(o.status)),
        inspections: scope.length === "long" ? inspectionsFor(store, f.id, t.id) : [],
        repairs: scope.length === "long" ? rs : [],
        forecast: fc.find((r) => r.facilityId === f.id && r.trade === t.id) ?? null,
      });
    }
  }
  return out;
}
